import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "context/ApiContext";
import { useCurrency } from "context/CurrencyContext";
import { t } from "i18next";
import LanguageSwitcher from "./LanguageSwitcher";

interface IPage {
  _id: string;
  title: string;
  slug: string;
  type?: "policy" | "static" | "custom";
  pageType?: string;
  isActive: boolean;
}

const Footer: React.FC = () => {
  const { systemInfo, api } = useApi();
  const { location, storeFallbackLocation } = useCurrency();
  const [staticPages, setStaticPages] = useState<IPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaticPages = async (): Promise<void> => {
      try {
        setLoading(true);
        // Fetch all static pages (including former policy pages)
        // Fetch both from /pages (CMS pages) and /page-builder/pages (system pages)
        const [cmsResponse, pbResponse] = await Promise.all([
          api
            .get("/pages?includeInactive=true")
            .catch(() => ({ data: { pages: [] } })),
          api
            .get("/page-builder/pages?includeUnpublished=true&limit=100")
            .catch(() => ({ data: { pages: [] } })),
        ]);

        const cmsPages = cmsResponse.data.pages || [];
        const pbPages = pbResponse.data.pages || [];

        // Merge pages, prioritizing CMS pages
        const pageMap = new Map<string, IPage>();

        // Add CMS pages (static, custom, and policy pages - all treated as static now)
        cmsPages.forEach((page: IPage) => {
          const pageType = page.type || page.pageType || "static";
          // Include static, custom, and policy pages (policy pages are now treated as static)
          if (
            pageType === "static" ||
            pageType === "custom" ||
            pageType === "policy"
          ) {
            pageMap.set(page._id, page);
          }
        });

        // Add page builder pages that are static or policy
        pbPages.forEach((page: IPage) => {
          const pageType = page.pageType || page.type || "static";
          if (
            (pageType === "static" ||
              pageType === "custom" ||
              pageType === "policy") &&
            !pageMap.has(page._id)
          ) {
            pageMap.set(page._id, page);
          }
        });

        // Filter only active pages
        const allPages = Array.from(pageMap.values());
        const activePages = allPages.filter((page: IPage) => page.isActive);
        setStaticPages(activePages);
      } catch (error) {
        // Silently fail - don't show errors in footer
        console.error("Error fetching static pages:", error);
        setStaticPages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStaticPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <footer className="bg-white border-store-accent p-6">
      <div className="max-w-7xl mx-auto">
        {/* Static Pages Links Section */}
        {!loading && staticPages.length > 0 && (
          <div className="mb-6 pb-6">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {staticPages.map((page) => (
                <Link
                  key={page._id}
                  to={`/pages/${page.slug}`}
                  className="text-store-primary-light hover:text-store-secondary text-sm font-medium transition-colors"
                >
                  {page.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Copyright and Language Switcher Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left text-store-primary-light text-xs">
            <p className="font-medium font-store">
              © {new Date().getFullYear()} ushoplify{" "}
              <span className="text-store-accent">•</span>{" "}
              {systemInfo?.version || "1.0.0"}
            </p>
            <p className="text-store-primary-light text-xs mt-2">
              {t("footer.copyright")}
            </p>
          </div>
          <div className="flex items-center justify-center gap-4">
            {location && (
              <div className="flex items-center gap-2 text-store-primary-light text-xs">
                <div>
                  <span className="font-medium">{location.country}</span>
                  {location.countryCode && (
                    <span className="text-store-accent ml-1">
                      ({location.countryCode})
                    </span>
                  )}
                </div>
                {storeFallbackLocation && storeFallbackLocation.countryCode !== location.countryCode && (
                  <>
                    <span className="text-store-accent">•</span>
                    <div className="text-store-primary-light/70">
                      Store: {storeFallbackLocation.country}
                      {storeFallbackLocation.countryCode && (
                        <span className="text-store-accent ml-1">
                          ({storeFallbackLocation.countryCode})
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
